import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { Search, TrendingUp, Star, Music, Users, Heart, Filter, Play, Clock, X, Bell, Mic, Wrench, Sparkles } from 'lucide-react';
import { useAuth } from '../../hooks/useAuth';
import { studentDashboardService } from '../../services/studentDashboardService';
import userService from '../../services/userService';
import { supabase } from '../../lib/supabase';
import Header from '../../components/ui/Header';
import LoadingSpinner from '../../components/ui/LoadingSpinner';
import NotificationPreferences from './components/NotificationPreferences';
import PersonalizedRecommendations from './components/PersonalizedRecommendations';

// Onboarding Modal Component
const OnboardingModal = ({ isOpen, onClose, onComplete, userId }) => {
  const [selectedMusicals, setSelectedMusicals] = useState([]);
  const [availableMusicals, setAvailableMusicals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (isOpen) {
      loadRandomMusicals();
    }
  }, [isOpen]);

  const loadRandomMusicals = async () => {
    try {
      setLoading(true);
      const { musicals, error: musicalsError } = await userService?.getRandomMusicals(30);
      
      if (musicalsError) {
        setError('Failed to load musicals. Please try again.');
      } else {
        setAvailableMusicals(musicals || []);
      }
    } catch (err) {
      setError('An error occurred while loading musicals.');
    } finally {
      setLoading(false);
    }
  };

  const toggleMusical = (musicalId) => {
    setSelectedMusicals(prev => {
      if (prev?.includes(musicalId)) {
        return prev?.filter(id => id !== musicalId);
      } else {
        return [...prev, musicalId];
      }
    });
  };

  const handleSubmit = async () => {
    if (selectedMusicals?.length === 0) {
      setError('Please select at least one musical');
      return;
    }

    try {
      setSaving(true);
      setError(null);
      
      // Verify user is authenticated
      if (!userId) {
        setError('Authentication error. Please refresh the page and try again.');
        return;
      }
      
      // Save favorites (the service will use auth.uid() internally)
      await userService?.saveFavoriteMusicals(userId, selectedMusicals);
      
      // Mark onboarding as completed
      await userService?.markOnboardingCompleted(userId);
      
      onComplete();
    } catch (err) {
      console.error('Error saving favorites:', err);
      
      // Provide specific error messages based on the error type
      let errorMessage = 'Failed to save your selections. ';
      
      if (err?.message?.includes('User profile not found')) {
        errorMessage += 'Your account setup is incomplete. Please log out and log in again.';
      } else if (err?.message?.includes('Authentication required')) {
        errorMessage += 'Your session has expired. Please refresh the page and log in again.';
      } else if (err?.message?.includes('foreign key')) {
        errorMessage += 'Some selected musicals are no longer available. Please try selecting different musicals.';
      } else {
        errorMessage += 'Please try again or contact support if the problem persists.';
      }
      
      setError(errorMessage);
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-70 z-50 flex items-center justify-center p-4">
      <div className="bg-gradient-to-br from-indigo-900 to-purple-900 rounded-2xl max-w-4xl w-full max-h-[95vh] flex flex-col overflow-hidden shadow-2xl">
        {/* Header */}
        <div className="bg-white bg-opacity-10 backdrop-blur-sm p-4 sm:p-6 border-b border-white border-opacity-20 flex-shrink-0">
          <h2 className="text-2xl sm:text-3xl font-bold text-white mb-2">Welcome to The Stage! 🎭</h2>
          <p className="text-blue-200 text-sm sm:text-base">
            Pick your favorite musicals to personalize your learning experience. Select at least one to get started!
          </p>
        </div>

        {/* Content - Made scrollable with flex-1 */}
        <div className="flex-1 overflow-y-auto p-4 sm:p-6">
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <LoadingSpinner />
            </div>
          ) : error ? (
            <div className="bg-red-500/10 border border-red-500 rounded-xl p-4 mb-4">
              <p className="text-red-200 text-sm">{error}</p>
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4">
              {availableMusicals?.map((musical) => (
                <div
                  key={musical?.id}
                  onClick={() => toggleMusical(musical?.id)}
                  className={`relative cursor-pointer rounded-xl overflow-hidden transition-all duration-200 ${
                    selectedMusicals?.includes(musical?.id)
                      ? 'ring-4 ring-yellow-400 scale-105' : 'hover:scale-102 hover:ring-2 hover:ring-white hover:ring-opacity-50'
                  }`}
                >
                  <img
                    src={musical?.posterUrl || '/assets/images/no_image.png'}
                    alt={musical?.title}
                    className="w-full aspect-[2/3] object-cover"
                  />
                  {selectedMusicals?.includes(musical?.id) && (
                    <div className="absolute top-2 right-2 bg-yellow-400 rounded-full p-1.5 sm:p-2">
                      <Heart className="w-4 h-4 sm:w-5 sm:h-5 text-indigo-900 fill-current" />
                    </div>
                  )}
                  <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black to-transparent p-2 sm:p-3">
                    <p className="text-white text-xs sm:text-sm font-medium truncate">{musical?.title}</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer - Always visible at bottom */}
        <div className="bg-white bg-opacity-10 backdrop-blur-sm p-4 sm:p-6 border-t border-white border-opacity-20 flex-shrink-0">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-3 sm:gap-0">
            <p className="text-white text-sm text-center sm:text-left">
              {selectedMusicals?.length} musical{selectedMusicals?.length !== 1 ? 's' : ''} selected
            </p>
            <button
              onClick={handleSubmit}
              disabled={saving || selectedMusicals?.length === 0}
              className={`w-full sm:w-auto px-6 py-3 rounded-xl font-medium transition-colors ${
                saving || selectedMusicals?.length === 0
                  ? 'bg-gray-500 cursor-not-allowed' : 'bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700'
              } text-white`}
            >
              {saving ? 'Saving...' : 'Get Started'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default function TheStageStudentDashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [showNotificationPrefs, setShowNotificationPrefs] = useState(false);

  // User progress state
  const [userProgress, setUserProgress] = useState(null);
  const [inProgressSongs, setInProgressSongs] = useState([]);
  const [recommendedRole, setRecommendedRole] = useState(null);
  const [favoriteMusicals, setFavoriteMusicals] = useState([]);

  // Popular content state
  const [popularMusicals, setPopularMusicals] = useState([]);
  const [popularRoles, setPopularRoles] = useState([]);
  const [popularSongs, setPopularSongs] = useState([]);

  // Search state
  const [searchQuery, setSearchQuery] = useState('');
  const [searchCategory, setSearchCategory] = useState('all');
  const [searchResults, setSearchResults] = useState(null);
  const [searchLoading, setSearchLoading] = useState(false);

  // NEW: Favorites filtering and sorting state
  const [favoritesFilter, setFavoritesFilter] = useState('all'); // 'all', 'broadway', 'off_broadway', etc.
  const [favoritesSort, setFavoritesSort] = useState('recent'); // 'recent', 'alphabetical', 'year'
  const [showFilterMenu, setShowFilterMenu] = useState(false);
  const [filteredAndSortedFavorites, setFilteredAndSortedFavorites] = useState([]);
  
  // NEW: In-progress songs from favorite musicals
  const [favoriteMusicalsProgress, setFavoriteMusicalsProgress] = useState([]);

  useEffect(() => {
    if (user?.id) {
      checkOnboardingStatus();
    }
  }, [user]);

  const checkOnboardingStatus = async () => {
    try {
      // Check if user has completed onboarding (has favorites)
      const { hasCompleted } = await userService?.hasCompletedOnboarding(user?.id);

      if (!hasCompleted) {
        setShowOnboarding(true);
        setLoading(false);
      } else {
        loadDashboardData();
      }
    } catch (err) {
      console.error('Error checking onboarding:', err);
      loadDashboardData();
    }
  };

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      setError('');

      // Load all dashboard data in parallel
      const [progress, songs, role, musicals, roles, popularSongsData, favorites] = await Promise.all([
        studentDashboardService?.getUserProgress(user?.id),
        studentDashboardService?.getInProgressSongs(user?.id),
        studentDashboardService?.getRecommendedRole(user?.id),
        studentDashboardService?.getPopularMusicals(12),
        studentDashboardService?.getPopularRoles(12),
        studentDashboardService?.getPopularSongs(12),
        userService?.getFavoriteMusicals(user?.id)
      ]);

      setUserProgress(progress);
      setInProgressSongs(songs);
      setRecommendedRole(role);
      setPopularMusicals(musicals);
      setPopularRoles(roles);
      setPopularSongs(popularSongsData);
      setFavoriteMusicals(favorites?.favorites || []);
      
      // NEW: Load in-progress songs from favorite musicals
      await loadFavoriteMusicalsProgress(favorites?.favorites || []);
    } catch (err) {
      console.error('Error loading dashboard:', err);
      setError(err?.message || 'Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  // NEW: Load in-progress songs from favorite musicals
  const loadFavoriteMusicalsProgress = async (favorites) => {
    try {
      if (!favorites || favorites?.length === 0) return;

      const musicalIds = favorites?.map(f => f?.musicalId);
      
      // Get songs from favorite musicals that have progress
      const { data: progressData, error: progressError } = await supabase
        ?.from('user_lesson_progress')
        ?.select(`
          id,
          lesson_section,
          lesson_status,
          video_progress_percentage,
          started_at,
          songs (
            id,
            title,
            musical_id,
            musicals (
              id,
              title,
              poster_url
            )
          )
        `)
        ?.eq('user_id', user?.id)
        ?.in('songs.musical_id', musicalIds)
        ?.in('lesson_status', ['started', 'available'])
        ?.order('started_at', { ascending: false })
        ?.limit(6);

      if (progressError) throw progressError;

      const formattedProgress = (progressData || [])?.map(p => ({
        progressId: p?.id,
        songId: p?.songs?.id,
        songTitle: p?.songs?.title,
        musicalId: p?.songs?.musicals?.id,
        musicalTitle: p?.songs?.musicals?.title,
        musicalPoster: p?.songs?.musicals?.poster_url,
        lessonSection: p?.lesson_section,
        lessonStatus: p?.lesson_status,
        progressPercentage: parseFloat(p?.video_progress_percentage || 0),
        startedAt: p?.started_at
      }));

      setFavoriteMusicalsProgress(formattedProgress);
    } catch (err) {
      console.error('Error loading favorites progress:', err);
    }
  };

  // NEW: Filter and sort favorites whenever favorites, filter, or sort changes
  useEffect(() => {
    if (favoriteMusicals?.length > 0) {
      let filtered = [...favoriteMusicals];

      // Apply genre filter
      if (favoritesFilter !== 'all') {
        filtered = filtered?.filter(m => m?.genre === favoritesFilter);
      }

      // Apply sorting
      switch (favoritesSort) {
        case 'alphabetical':
          filtered?.sort((a, b) => a?.title?.localeCompare(b?.title));
          break;
        case 'year':
          filtered?.sort((a, b) => (b?.yearPremiered || 0) - (a?.yearPremiered || 0));
          break;
        case 'recent':
        default:
          filtered?.sort((a, b) => new Date(b?.addedAt) - new Date(a?.addedAt));
          break;
      }

      setFilteredAndSortedFavorites(filtered);
    }
  }, [favoriteMusicals, favoritesFilter, favoritesSort]);

  const handleOnboardingComplete = () => {
    setShowOnboarding(false);
    loadDashboardData();
  };

  const handleSearch = async (e) => {
    e?.preventDefault();
    if (!searchQuery?.trim()) {
      setSearchResults(null);
      return;
    }

    try {
      setSearchLoading(true);
      const results = await studentDashboardService?.searchContent(searchQuery, searchCategory);
      setSearchResults(results);
    } catch (err) {
      console.error('Error searching:', err);
    } finally {
      setSearchLoading(false);
    }
  };

  const navigateToMusical = (musicalId) => {
    navigate(`/musical-progress-page?id=${musicalId}`);
  };

  const navigateToRole = (roleId) => {
    navigate(`/role-detail-page?id=${roleId}`);
  };

  const navigateToSong = (songId) => {
    navigate(`/song-detail-page?id=${songId}`);
  };

  const toggleFavorite = async (musicalId) => {
    try {
      const isFavorite = favoriteMusicals?.some(m => m?.id === musicalId);
      
      if (isFavorite) {
        await userService?.removeFavoriteMusical(user?.id, musicalId);
        setFavoriteMusicals(prev => prev?.filter(m => m?.id !== musicalId));
      } else {
        await userService?.addFavoriteMusical(user?.id, musicalId);
        // Reload to get the full musical object
        await loadFavoriteMusicals();
      }
    } catch (error) {
      console.error('Error toggling favorite:', error);
      
      // Show user-friendly error message
      let errorMessage = error?.message?.includes('Authentication') 
        ? 'Session expired. Please refresh the page.' :'Failed to update favorites. Please try again.';
      
      // You might want to add a toast notification here instead of console.error
      alert(errorMessage);
    }
  };

  const loadFavoriteMusicals = async () => {
    try {
      const favorites = await userService?.getFavoriteMusicals(user?.id);
      setFavoriteMusicals(favorites?.favorites || []);
    } catch (err) {
      console.error('Error loading favorites:', err);
    }
  };

  // NEW: Clear all filters
  const clearFilters = () => {
    setFavoritesFilter('all');
    setFavoritesSort('recent');
  };

  // NEW: Get unique genres from favorites
  const getAvailableGenres = () => {
    const genres = [...new Set(favoriteMusicals?.map(m => m?.genre)?.filter(Boolean))];
    return genres;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#6e32b9] to-[#32215d]">
        <Header />
        <div className="flex items-center justify-center h-[calc(100vh-80px)]">
          <LoadingSpinner />
        </div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>The Stage - RoleCall Student Dashboard</title>
        <meta name="description" content="Your personalized musical theater learning dashboard with progress tracking, recommendations, and popular content" />
      </Helmet>
      
      {/* Onboarding Modal */}
      <OnboardingModal
        isOpen={showOnboarding}
        onClose={() => setShowOnboarding(false)}
        onComplete={handleOnboardingComplete}
        userId={user?.id}
      />

      {/* NEW: Notification Preferences Modal */}
      <NotificationPreferences
        isOpen={showNotificationPrefs}
        onClose={() => setShowNotificationPrefs(false)}
        userId={user?.id}
      />

      <div className="min-h-screen bg-gradient-to-br from-[#6e32b9] to-[#32215d]">
        <Header />

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Error Display */}
          {error && (
            <div className="mb-6 bg-red-500/10 border border-red-500 rounded-xl p-4">
              <p className="text-red-200 text-sm">{error}</p>
            </div>
          )}

          {/* NEW: Notification Settings Banner */}
          <div className="bg-gradient-to-r from-purple-600/30 to-pink-600/30 backdrop-blur-md rounded-2xl p-4 mb-6 border border-purple-400/30">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Bell className="w-5 h-5 text-purple-300" />
                <div>
                  <p className="text-white font-medium">Stay Updated on Your Favorites</p>
                  <p className="text-white/70 text-sm">Get email alerts when new content is added to musicals you love</p>
                </div>
              </div>
              <button
                onClick={() => setShowNotificationPrefs(true)}
                className="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg font-medium transition-colors flex-shrink-0"
              >
                Manage Notifications
              </button>
            </div>
          </div>

          {/* User Progress Section */}
          <div className="bg-white/10 backdrop-blur-md rounded-2xl p-6 mb-8 border border-white/20">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {/* XP Progress */}
              <div className="flex flex-col">
                <div className="flex items-center gap-2 mb-2">
                  <TrendingUp className="w-5 h-5 text-yellow-400" />
                  <h3 className="text-white font-semibold">XP Points</h3>
                </div>
                <div className="bg-white/5 rounded-xl p-4">
                  <p className="text-3xl font-bold text-white">{userProgress?.totalXp || 0}</p>
                  <p className="text-white/70 text-sm mt-1">Level {userProgress?.currentLevel || 1}</p>
                  <div className="mt-3 bg-white/10 rounded-full h-2 overflow-hidden">
                    <div 
                      className="bg-gradient-to-r from-yellow-400 to-orange-500 h-full rounded-full transition-all duration-300"
                      style={{ width: `${((userProgress?.totalXp || 0) % 100)}%` }}
                    />
                  </div>
                </div>
              </div>

              {/* Streak */}
              <div className="flex flex-col">
                <div className="flex items-center gap-2 mb-2">
                  <Star className="w-5 h-5 text-orange-400" />
                  <h3 className="text-white font-semibold">Daily Streak</h3>
                </div>
                <div className="bg-white/5 rounded-xl p-4">
                  <p className="text-3xl font-bold text-white">{userProgress?.dailyStreak || 0} days</p>
                  <p className="text-white/70 text-sm mt-1">
                    Best: {userProgress?.longestStreak || 0} days
                  </p>
                </div>
              </div>

              {/* In Progress Songs */}
              <div className="flex flex-col">
                <div className="flex items-center gap-2 mb-2">
                  <Music className="w-5 h-5 text-purple-400" />
                  <h3 className="text-white font-semibold">In Progress</h3>
                </div>
                <div className="bg-white/5 rounded-xl p-4">
                  <p className="text-3xl font-bold text-white">{inProgressSongs?.length || 0}</p>
                  <p className="text-white/70 text-sm mt-1">Active songs</p>
                </div>
              </div>
            </div>
          </div>

          {/* NEW: AI-Powered Personalized Recommendations */}
          <div className="mb-8">
            <PersonalizedRecommendations userId={user?.id} />
          </div>

          {/* Recommended Next Role */}
          {recommendedRole && (
            <div className="bg-white/10 backdrop-blur-md rounded-2xl p-6 mb-8 border border-white/20">
              <h2 className="text-xl font-bold text-white mb-4">Recommended Next Role</h2>
              <div 
                className="flex gap-4 cursor-pointer hover:bg-white/5 rounded-xl p-4 transition-all"
                onClick={() => navigateToRole(recommendedRole?.roleId)}
              >
                <img 
                  src={recommendedRole?.musicalPoster || '/assets/images/no_image.png'} 
                  alt={recommendedRole?.musicalTitle}
                  className="w-24 h-36 object-cover rounded-lg"
                />
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-white">{recommendedRole?.characterName}</h3>
                  <p className="text-purple-300 text-sm mb-2">{recommendedRole?.musicalTitle}</p>
                  <div className="flex gap-2 mb-2">
                    <span className="px-2 py-1 bg-purple-500/30 text-purple-200 rounded-lg text-xs">
                      {recommendedRole?.voiceType}
                    </span>
                    {recommendedRole?.vocalRange && (
                      <span className="px-2 py-1 bg-blue-500/30 text-blue-200 rounded-lg text-xs">
                        {recommendedRole?.vocalRange}
                      </span>
                    )}
                  </div>
                  <p className="text-white/70 text-sm line-clamp-2">{recommendedRole?.description}</p>
                </div>
              </div>
            </div>
          )}

          {/* Search Bar */}
          <div className="bg-white/10 backdrop-blur-md rounded-2xl p-6 mb-8 border border-white/20">
            <form onSubmit={handleSearch} className="space-y-4">
              <div className="flex gap-2">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-white/50" />
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e?.target?.value)}
                    placeholder="Search for musicals, roles, or songs..."
                    className="w-full bg-white/5 border border-white/20 rounded-xl pl-10 pr-4 py-3 text-white placeholder-white/50 focus:outline-none focus:border-purple-400"
                  />
                </div>
                <button
                  type="submit"
                  disabled={searchLoading}
                  className="px-6 py-3 bg-purple-600 hover:bg-purple-700 text-white rounded-xl font-medium transition-colors disabled:opacity-50"
                >
                  {searchLoading ? 'Searching...' : 'Search'}
                </button>
              </div>
              <div className="flex gap-2">
                {['all', 'musicals', 'roles', 'songs']?.map((category) => (
                  <button
                    key={category}
                    type="button"
                    onClick={() => setSearchCategory(category)}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                      searchCategory === category
                        ? 'bg-purple-600 text-white' :'bg-white/5 text-white/70 hover:bg-white/10'
                    }`}
                  >
                    {category?.charAt(0)?.toUpperCase() + category?.slice(1)}
                  </button>
                ))}
              </div>
            </form>

            {/* Search Results */}
            {searchResults && (
              <div className="mt-6 space-y-4">
                {searchResults?.musicals?.length > 0 && (
                  <div>
                    <h3 className="text-white font-semibold mb-3">Musicals</h3>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      {searchResults?.musicals?.map((musical) => (
                        <div
                          key={musical?.id}
                          onClick={() => navigateToMusical(musical?.id)}
                          className="cursor-pointer hover:scale-105 transition-transform"
                        >
                          <img
                            src={musical?.posterUrl || '/assets/images/no_image.png'}
                            alt={musical?.title}
                            className="w-full aspect-[2/3] object-cover rounded-lg mb-2"
                          />
                          <p className="text-white text-sm font-medium truncate">{musical?.title}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {searchResults?.roles?.length > 0 && (
                  <div>
                    <h3 className="text-white font-semibold mb-3">Roles</h3>
                    <div className="space-y-2">
                      {searchResults?.roles?.map((role) => (
                        <div
                          key={role?.id}
                          onClick={() => navigateToRole(role?.id)}
                          className="flex gap-3 p-3 bg-white/5 rounded-lg cursor-pointer hover:bg-white/10 transition-colors"
                        >
                          <img
                            src={role?.musicalPoster || '/assets/images/no_image.png'}
                            alt={role?.musicalTitle}
                            className="w-16 h-24 object-cover rounded"
                          />
                          <div className="flex-1">
                            <p className="text-white font-medium">{role?.characterName}</p>
                            <p className="text-purple-300 text-sm">{role?.musicalTitle}</p>
                            <span className="inline-block mt-1 px-2 py-1 bg-purple-500/30 text-purple-200 rounded text-xs">
                              {role?.voiceType}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {searchResults?.songs?.length > 0 && (
                  <div>
                    <h3 className="text-white font-semibold mb-3">Songs</h3>
                    <div className="space-y-2">
                      {searchResults?.songs?.map((song) => (
                        <div
                          key={song?.id}
                          onClick={() => navigateToSong(song?.id)}
                          className="flex gap-3 p-3 bg-white/5 rounded-lg cursor-pointer hover:bg-white/10 transition-colors"
                        >
                          <Music className="w-10 h-10 text-purple-400" />
                          <div className="flex-1">
                            <p className="text-white font-medium">{song?.title}</p>
                            <p className="text-purple-300 text-sm">{song?.musicalTitle}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* NEW: Continue Learning from Favorites - Show BEFORE favorites list */}
          {favoriteMusicalsProgress?.length > 0 && (
            <div className="bg-gradient-to-r from-purple-600/30 to-indigo-600/30 backdrop-blur-md rounded-2xl p-6 mb-8 border border-purple-400/30">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <Play className="w-6 h-6 text-purple-300" />
                  <h2 className="text-xl font-bold text-white">Continue Learning from Your Favorites</h2>
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {favoriteMusicalsProgress?.map((progress) => (
                  <div
                    key={progress?.progressId}
                    onClick={() => navigateToSong(progress?.songId)}
                    className="bg-white/10 rounded-xl p-4 cursor-pointer hover:bg-white/20 transition-all group"
                  >
                    <div className="flex gap-3">
                      <img
                        src={progress?.musicalPoster || '/assets/images/no_image.png'}
                        alt={progress?.musicalTitle}
                        className="w-16 h-24 object-cover rounded-lg"
                      />
                      <div className="flex-1">
                        <h3 className="text-white font-medium text-sm line-clamp-1 group-hover:text-purple-300 transition-colors">
                          {progress?.songTitle}
                        </h3>
                        <p className="text-purple-300 text-xs mb-2 line-clamp-1">{progress?.musicalTitle}</p>
                        
                        {/* Progress bar */}
                        <div className="mb-2">
                          <div className="flex items-center justify-between mb-1">
                            <span className="text-white/70 text-xs">{progress?.lessonSection?.replace('level', 'Level ')}</span>
                            <span className="text-white/70 text-xs">{Math.round(progress?.progressPercentage)}%</span>
                          </div>
                          <div className="bg-white/20 rounded-full h-2 overflow-hidden">
                            <div 
                              className="bg-gradient-to-r from-purple-400 to-pink-400 h-full rounded-full transition-all duration-300"
                              style={{ width: `${progress?.progressPercentage}%` }}
                            />
                          </div>
                        </div>
                        
                        <div className="flex items-center gap-1 text-white/60 text-xs">
                          <Clock className="w-3 h-3" />
                          <span>Started {new Date(progress?.startedAt)?.toLocaleDateString()}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* ENHANCED: Favorite Musicals Section with Filtering and Sorting */}
          {favoriteMusicals?.length > 0 && (
            <div className="bg-white/10 backdrop-blur-md rounded-2xl p-6 mb-8 border border-white/20">
              {/* Header with Filter and Sort Controls */}
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
                <div className="flex items-center gap-2">
                  <Heart className="w-6 h-6 text-pink-400 fill-current" />
                  <h2 className="text-xl font-bold text-white">My Favorite Musicals</h2>
                  <span className="px-2 py-1 bg-pink-500/30 text-pink-200 rounded-lg text-sm font-medium">
                    {filteredAndSortedFavorites?.length}
                  </span>
                </div>
                
                <div className="flex items-center gap-2 w-full sm:w-auto">
                  {/* Filter Button */}
                  <div className="relative">
                    <button
                      onClick={() => setShowFilterMenu(!showFilterMenu)}
                      className={`flex items-center gap-2 px-4 py-2 rounded-xl font-medium transition-colors ${
                        favoritesFilter !== 'all' ?'bg-pink-600 text-white' :'bg-white/10 text-white/70 hover:bg-white/20'
                      }`}
                    >
                      <Filter className="w-4 h-4" />
                      <span className="hidden sm:inline">Filter</span>
                    </button>
                    
                    {/* Filter Dropdown */}
                    {showFilterMenu && (
                      <div className="absolute right-0 mt-2 w-48 bg-gray-900 rounded-xl shadow-xl border border-white/20 z-10">
                        <div className="p-2">
                          <button
                            onClick={() => { setFavoritesFilter('all'); setShowFilterMenu(false); }}
                            className={`w-full text-left px-3 py-2 rounded-lg transition-colors ${
                              favoritesFilter === 'all' ? 'bg-pink-600 text-white' : 'text-white/70 hover:bg-white/10'
                            }`}
                          >
                            All Genres
                          </button>
                          {getAvailableGenres()?.map(genre => (
                            <button
                              key={genre}
                              onClick={() => { setFavoritesFilter(genre); setShowFilterMenu(false); }}
                              className={`w-full text-left px-3 py-2 rounded-lg transition-colors mt-1 ${
                                favoritesFilter === genre ? 'bg-pink-600 text-white' : 'text-white/70 hover:bg-white/10'
                              }`}
                            >
                              {genre?.replace('_', ' ')?.replace(/\b\w/g, l => l?.toUpperCase())}
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Sort Dropdown */}
                  <select
                    value={favoritesSort}
                    onChange={(e) => setFavoritesSort(e?.target?.value)}
                    className="px-4 py-2 bg-white/10 border border-white/20 rounded-xl text-white focus:outline-none focus:border-purple-400"
                  >
                    <option value="recent" className="bg-gray-900">Recently Added</option>
                    <option value="alphabetical" className="bg-gray-900">A-Z</option>
                    <option value="year" className="bg-gray-900">Year</option>
                  </select>

                  {/* Clear Filters */}
                  {(favoritesFilter !== 'all' || favoritesSort !== 'recent') && (
                    <button
                      onClick={clearFilters}
                      className="p-2 bg-white/10 hover:bg-white/20 rounded-xl transition-colors"
                      title="Clear filters"
                    >
                      <X className="w-4 h-4 text-white" />
                    </button>
                  )}
                </div>
              </div>

              {/* Musicals Grid - Enhanced with Playful Design */}
              {filteredAndSortedFavorites?.length === 0 ? (
                <div className="text-center py-12">
                  <p className="text-white/70 text-lg">No musicals match your filters</p>
                  <button
                    onClick={clearFilters}
                    className="mt-4 px-6 py-2 bg-pink-600 hover:bg-pink-700 text-white rounded-xl font-medium transition-colors"
                  >
                    Clear Filters
                  </button>
                </div>
              ) : (
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
                  {filteredAndSortedFavorites?.map((musical) => (
                    <div
                      key={musical?.musicalId}
                      className="group relative cursor-pointer"
                    >
                      {/* Musical Card */}
                      <div 
                        onClick={() => navigateToMusical(musical?.musicalId)}
                        className="relative rounded-xl overflow-hidden transition-all duration-300 transform group-hover:scale-105 group-hover:shadow-2xl"
                      >
                        <img
                          src={musical?.posterUrl || '/assets/images/no_image.png'}
                          alt={musical?.title}
                          className="w-full aspect-[2/3] object-cover"
                        />
                        
                        {/* Gradient Overlay */}
                        <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/40 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                          <div className="absolute bottom-0 left-0 right-0 p-3">
                            <p className="text-white text-sm font-medium line-clamp-2">{musical?.title}</p>
                            {musical?.composer && (
                              <p className="text-white/70 text-xs mt-1 line-clamp-1">{musical?.composer}</p>
                            )}
                            {musical?.yearPremiered && (
                              <span className="inline-block mt-2 px-2 py-1 bg-purple-500/30 text-purple-200 rounded text-xs">
                                {musical?.yearPremiered}
                              </span>
                            )}
                          </div>
                        </div>

                        {/* Favorite Badge */}
                        <div className="absolute top-2 right-2 z-10">
                          <button
                            onClick={(e) => {
                              e?.stopPropagation();
                              toggleFavorite(musical?.musicalId);
                            }}
                            className="p-2 bg-pink-500/90 hover:bg-pink-600 rounded-full transition-all duration-200 transform hover:scale-110"
                          >
                            <Heart className="w-4 h-4 text-white fill-current" />
                          </button>
                        </div>

                        {/* Genre Badge */}
                        {musical?.genre && (
                          <div className="absolute top-2 left-2">
                            <span className="px-2 py-1 bg-black/60 backdrop-blur-sm text-white text-xs rounded-lg">
                              {musical?.genre?.replace('_', ' ')?.replace(/\b\w/g, l => l?.toUpperCase())}
                            </span>
                          </div>
                        )}
                      </div>

                      {/* Title Below Card (Always Visible) */}
                      <div className="mt-2 px-1">
                        <p className="text-white text-sm font-medium truncate">{musical?.title}</p>
                        {musical?.composer && (
                          <p className="text-white/60 text-xs truncate">{musical?.composer}</p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Popular Musicals */}
          <div className="bg-white/10 backdrop-blur-md rounded-2xl p-6 mb-8 border border-white/20">
            <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
              <TrendingUp className="w-6 h-6 text-yellow-400" />
              Popular Musicals
            </h2>
            <div className="overflow-x-auto">
              <div className="flex gap-4 pb-4">
                {popularMusicals?.map((musical) => (
                  <div
                    key={musical?.id}
                    onClick={() => navigateToMusical(musical?.id)}
                    className="flex-shrink-0 w-40 cursor-pointer hover:scale-105 transition-transform"
                  >
                    <img
                      src={musical?.posterUrl || '/assets/images/no_image.png'}
                      alt={musical?.title}
                      className="w-full aspect-[2/3] object-cover rounded-lg mb-2"
                    />
                    <p className="text-white text-sm font-medium truncate">{musical?.title}</p>
                    <p className="text-white/60 text-xs">{musical?.genre}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Popular Roles */}
          <div className="bg-white/10 backdrop-blur-md rounded-2xl p-6 mb-8 border border-white/20">
            <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
              <Users className="w-6 h-6 text-purple-400" />
              Popular Roles
            </h2>
            <div className="overflow-x-auto">
              <div className="flex gap-4 pb-4">
                {popularRoles?.map((role) => (
                  <div
                    key={role?.id}
                    onClick={() => navigateToRole(role?.id)}
                    className="flex-shrink-0 w-48 cursor-pointer hover:scale-105 transition-transform"
                  >
                    <img
                      src={role?.musicalPoster || '/assets/images/no_image.png'}
                      alt={role?.musicalTitle}
                      className="w-full aspect-[2/3] object-cover rounded-lg mb-2"
                    />
                    <p className="text-white text-sm font-medium truncate">{role?.characterName}</p>
                    <p className="text-purple-300 text-xs">{role?.musicalTitle}</p>
                    <span className="inline-block mt-1 px-2 py-1 bg-purple-500/30 text-purple-200 rounded text-xs">
                      {role?.voiceType}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Popular Songs */}
          <div className="bg-white/10 backdrop-blur-md rounded-2xl p-6 border border-white/20">
            <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
              <Music className="w-6 h-6 text-orange-400" />
              Popular Songs
            </h2>
            <div className="overflow-x-auto">
              <div className="flex gap-4 pb-4">
                {popularSongs?.map((song) => (
                  <div
                    key={song?.id}
                    onClick={() => navigateToSong(song?.id)}
                    className="flex-shrink-0 w-48 cursor-pointer hover:scale-105 transition-transform"
                  >
                    <div className="bg-white/5 rounded-lg p-4 mb-2">
                      <Music className="w-12 h-12 text-purple-400 mx-auto mb-2" />
                      <p className="text-white text-sm font-medium text-center truncate">{song?.title}</p>
                      <p className="text-purple-300 text-xs text-center">{song?.musicalTitle}</p>
                      {song?.actNumber && (
                        <p className="text-white/50 text-xs text-center mt-1">Act {song?.actNumber}</p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* NEW: Tools Section - Pitch Training and Learning Tools */}
          <div className="bg-gradient-to-r from-indigo-600/30 to-purple-600/30 backdrop-blur-md rounded-2xl p-6 mt-8 border border-indigo-400/30">
            <div className="flex items-center gap-3 mb-6">
              <Wrench className="w-6 h-6 text-indigo-300" />
              <h2 className="text-xl font-bold text-white">Student Tools</h2>
              <span className="px-3 py-1 bg-indigo-500/30 text-indigo-200 rounded-lg text-sm font-medium">
                Training & Practice
              </span>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Pitch Coach Tool Card */}
              <div
                onClick={() => navigate('/student-pitch-coach-tool')}
                className="bg-gradient-to-br from-purple-600/40 to-pink-600/40 hover:from-purple-600/60 hover:to-pink-600/60 backdrop-blur-sm rounded-xl p-6 cursor-pointer transition-all duration-300 transform hover:scale-105 hover:shadow-2xl border border-purple-400/30 group"
              >
                <div className="flex items-start gap-4">
                  <div className="p-3 bg-purple-500/30 rounded-xl group-hover:bg-purple-500/50 transition-colors">
                    <Mic className="w-8 h-8 text-purple-200" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-lg font-bold text-white mb-2 group-hover:text-purple-200 transition-colors">
                      🎤 Pitch Coach
                    </h3>
                    <p className="text-white/80 text-sm mb-3">
                      Real-time pitch training with MIDI playback. Practice your vocal accuracy with instant feedback and theatre-friendly scoring.
                    </p>
                    <div className="flex flex-wrap gap-2">
                      <span className="px-2 py-1 bg-purple-500/30 text-purple-100 rounded-lg text-xs font-medium">
                        Real-time Feedback
                      </span>
                      <span className="px-2 py-1 bg-pink-500/30 text-pink-100 rounded-lg text-xs font-medium">
                        MIDI Playback
                      </span>
                      <span className="px-2 py-1 bg-indigo-500/30 text-indigo-100 rounded-lg text-xs font-medium">
                        Scoring System
                      </span>
                    </div>
                  </div>
                </div>
                <div className="mt-4 flex items-center justify-between">
                  <span className="text-white/60 text-xs">✨ Improve your pitch accuracy</span>
                  <span className="text-purple-200 text-sm font-medium group-hover:text-white transition-colors">
                    Start Training →
                  </span>
                </div>
              </div>

              {/* NEW: Audition Songs Tool Card */}
              <div
                onClick={() => navigate('/student-audition-songs-tool')}
                className="bg-gradient-to-br from-pink-600/40 to-purple-600/40 hover:from-pink-600/60 hover:to-purple-600/60 backdrop-blur-sm rounded-xl p-6 cursor-pointer transition-all duration-300 transform hover:scale-105 hover:shadow-2xl border border-pink-400/30 group"
              >
                <div className="flex items-start gap-4">
                  <div className="p-3 bg-pink-500/30 rounded-xl group-hover:bg-pink-500/50 transition-colors">
                    <Sparkles className="w-8 h-8 text-pink-200" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-lg font-bold text-white mb-2 group-hover:text-pink-200 transition-colors">
                      🎭 Audition Songs
                    </h3>
                    <p className="text-white/80 text-sm mb-3">
                      Get AI-powered audition song recommendations tailored to your chosen role and vocal profile.
                    </p>
                    <div className="flex flex-wrap gap-2">
                      <span className="px-2 py-1 bg-pink-500/30 text-pink-100 rounded-lg text-xs font-medium">
                        AI Recommendations
                      </span>
                      <span className="px-2 py-1 bg-purple-500/30 text-purple-100 rounded-lg text-xs font-medium">
                        Role Analysis
                      </span>
                      <span className="px-2 py-1 bg-indigo-500/30 text-indigo-100 rounded-lg text-xs font-medium">
                        Audition Strategy
                      </span>
                    </div>
                  </div>
                </div>
                <div className="mt-4 flex items-center justify-between">
                  <span className="text-white/60 text-xs">🌟 Find your perfect audition song</span>
                  <span className="text-pink-200 text-sm font-medium group-hover:text-white transition-colors">
                    Get Recommendations →
                  </span>
                </div>
              </div>
            </div>

            {/* Quick Tips Section */}
            <div className="mt-6 p-4 bg-white/5 rounded-xl border border-white/10">
              <p className="text-white/80 text-sm">
                💡 <strong>Pro Tip:</strong> Use the Audition Songs tool to discover the perfect songs for your next audition. Our AI analyzes the role's requirements and suggests songs that showcase your vocal strengths!
              </p>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}